import { ArrowLeft } from 'react-feather';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddLotteryMutation as AddLottery } from '__generated__/graphql';

import { assetUrl, encodeUrl, isGraphQLValidationError } from '../../../common/helpers';
import AddLotteryForm from './AddLotteryForm';
import EventPicker, { Event } from '../../Events/EventPicker';
import GetEventsCountQuery from '../GetEventsCountQuery';
import GetLotteriesForEventQuery from './GetLotteriesForEventQuery';
import LotteryCard from './LotteryCard';
import NoEventsCard from '../NoEventsCard';
import UI from '../../../common/UI';
import useDocumentTitle from '../../../common/useDocumentTitle';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useRecentEventId from '../../Common/useRecentEventId';
import useScroll from '../../../common/useScroll';

export interface LotteriesPageProps {
  lotteryId?: string;
}

const LotteriesPage = ({ lotteryId }: LotteriesPageProps) => {
  const { t } = useTranslation();
  const { scrollToIfInvisible } = useScroll();
  const history = useHistory();
  const project = useProject();
  const recentEvent = useRecentEventId();

  useDocumentTitle(t('lotteries'));

  const { data: eventsData } = useQuery(
    GetEventsCountQuery,
    {
      variables: {
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { data, loading, refetch } = useQuery(
    GetLotteriesForEventQuery,
    {
      variables: {
        project_id: project.id,
        event_id: recentEvent.id,
      },
      skip: !recentEvent.id,
      onError: (error) => {
        if (isGraphQLValidationError(error)) {
          // Recent event no longer exists, reset it.
          recentEvent.reset();
        }
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const event = data?.project.event.id === recentEvent.id ? data?.project.event : null;
  const lotteries = event?.lotteries;

  const [openAddLotteryModal, closeAddLotteryModal] = useModal(() => {
    const handleSuccess = (lottery: AddLottery['addLottery']) => {
      closeAddLotteryModal();
      history.push(`/${project.id}/participants/lotteries/${lottery.id}?${encodeUrl({ event: recentEvent.id })}`);
    };

    return (
      <UI.Modal maxWidth={600} close={closeAddLotteryModal}>
        <AddLotteryForm
          event={event}
          onSuccess={handleSuccess}
        />
      </UI.Modal>
    );
  }, [project, event]);

  useEffect(() => {
    if (event && lotteries.filter(({ id }) => id === lotteryId).length === 0) {
      refetch().then(() => {
        scrollToIfInvisible(`LotteryCard_${lotteryId}`, { gutter: 0.5 });
      });
    }
  }, [lotteryId]);

  const handleEventChange = (event: Event) => {
    history.push(`/${project.id}/participants/lotteries?${encodeUrl({ event: event.id })}`);
  };

  if (!eventsData || (recentEvent.id && !event)) {
    return <UI.PageLoader />;
  }

  if (eventsData.project.events.total === 0) {
    return <NoEventsCard />;
  }

  return (
    <UI.Section>
      <UI.GridContainer>
        <UI.FlexContainer
          sc={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <UI.SectionHeading sc={{ inlineBlock: true }}>
            {t('lotteries')}
          </UI.SectionHeading>
        </UI.FlexContainer>

        <UI.GridContainer sc={{ columns: ['1fr', '1fr 3fr'] }} style={{ zIndex: 10 }}>
          <UI.Div>
            <EventPicker
              eventId={recentEvent.id}
              onChange={handleEventChange}
              nullable={false}
              renderActive={(event) => (
                <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                  {event?.title || t('choose_event')}
                </UI.Span>
              )}
            />
          </UI.Div>
          {!recentEvent.id && (
            <UI.Div>
              <UI.Card
                sc={{ py: 0 }}
                style={{ lineHeight: '40px', borderRadius: '8px !important' }}
              >
                <UI.Icon sc={{ muted: true }}>
                  <ArrowLeft />
                </UI.Icon>
                {' '}
                {t('select_event_first')}
              </UI.Card>
            </UI.Div>
          )}
          {event && lotteries.length > 0 && (
            <UI.Div sc={{ textAlign: 'right' }}>
              <UI.Button
                onClick={openAddLotteryModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_lottery')}
              </UI.Button>
            </UI.Div>
          )}
        </UI.GridContainer>

        {event && (
          <UI.GridContainer sc={{ muted: loading, gutter: 0.5 }} style={{ zIndex: 1 }}>
            {lotteries.map((lottery) => (
              <LotteryCard
                lottery={lottery}
                event={event}
                active={lotteryId === lottery.id}
                muted={lotteryId && lotteryId !== lottery.id}
                key={lottery.id}
              />
            ))}
            {lotteries.length === 0 && (
              <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
                <UI.Div style={{ maxWidth: 600, margin: '0 auto' }}>
                  <img src={assetUrl('images/participant.svg')} alt="" height={120} />
                  <UI.H3 sc={{ mb: 2 }}>
                    {t('lotteries')}
                  </UI.H3>
                  <UI.Paragraph>
                    {t('lotteries_get_started')}
                  </UI.Paragraph>
                  <UI.Button
                    onClick={() => openAddLotteryModal()}
                    sc={{ brand: 'secondary' }}
                  >
                    {t('add_lottery')}
                  </UI.Button>
                </UI.Div>
              </UI.Card>
            )}
          </UI.GridContainer>
        )}

        {!recentEvent.id && (
          <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
            <UI.Div style={{ maxWidth: 600, margin: '0 auto' }}>
              <img src={assetUrl('images/participant.svg')} alt="" height={120} />
              <UI.H3 sc={{ mb: 2 }}>
                {t('lotteries')}
              </UI.H3>
              <UI.Paragraph sc={{ mb: 0 }}>
                {t('lotteries_get_started')}
              </UI.Paragraph>
            </UI.Div>
          </UI.Card>
        )}
      </UI.GridContainer>
    </UI.Section>
  );
};

export default LotteriesPage;
